import { Group } from '@thive/fddm-types';

import { areasToFlatCollection } from '../helpers';
import { templatesToFlatCollection } from '../helpers/helpers';
import { useFloorplanDesignStore } from '../store/useFloorplanDesignStore';
import { isRegularObstacle, isWallObstacle } from '../helpers/typeguards';

type FloorplanDesignState = ReturnType<typeof useFloorplanDesignStore.getState>;

export const selectAreasByIds = (state: FloorplanDesignState, ids: string[]) =>
  areasToFlatCollection(state.getAreasByIds(ids));

export const selectObstaclesByIds = (state: FloorplanDesignState, ids: string[]) =>
  state.getObstaclesByIds(ids);

export const selectTemplatesByIds = (state: FloorplanDesignState, ids: string[]) =>
  templatesToFlatCollection(state.getTemplatesByIds(ids));
export const selectLassieMinisByIds = (state: FloorplanDesignState, ids: string[]) =>
  state.getLassieMiniTemplatesByIds(ids);
export const selectShuttleRacksByIds = (state: FloorplanDesignState, ids: string[]) =>
  state.getShuttleRackTemplatesByIds(ids);
export const selectHikStoragesByIds = (state: FloorplanDesignState, ids: string[]) =>
  state.getHikStorageTemplatesByIds(ids);
export const selectLassiesByIds = (state: FloorplanDesignState, ids: string[]) =>
  state.getLassieTemplatesByIds(ids);

export const selectLogicalSpacesByIds = (state: FloorplanDesignState, ids: string[]) =>
  state.getLogicalSpacesByIds(ids);

export const selectGroupsByIds = (state: FloorplanDesignState, ids: string[]) =>
  state.getGroupsByIds(ids);

export const selectFlowsByIds = (state: FloorplanDesignState, ids: string[]) =>
  state.getFlowsByIds(ids);

/** @description The ids of the items in a group, including those in its nested groups */
export const getGroupItemIds = ({ areaIds, nestedGroups }: Group): string[] => [
  ...areaIds,
  ...nestedGroups.flatMap(getGroupItemIds),
];

/** @description The ids of the groups nested in a group, at any depth */
const getNestedGroupIds = ({ nestedGroups }: Group): string[] =>
  nestedGroups.flatMap((nestedGroup) => [nestedGroup.id, ...getNestedGroupIds(nestedGroup)]);

/**
 * @description The selected ids without what's inside a selected template or group (e.g. after
 * select all): a template or group is edited as a whole, never through its own items
 */
export const selectTopLevelSelectedIds = (state: FloorplanDesignState, ids: string[]) => {
  const containedIds = new Set([
    ...selectTemplatesByIds(state, ids).flatMap((template) => template.group.areaIds),
    ...selectGroupsByIds(state, ids).flatMap((group) => [
      ...getGroupItemIds(group),
      ...getNestedGroupIds(group),
    ]),
  ]);
  return ids.filter((id) => !containedIds.has(id));
};

/**
 * @description The ids the dimensions edit: the top-level selected ids, with every selected group
 * replaced by its items (a group is moved and rotated as one block)
 */
export const selectDimensionItemIds = (state: FloorplanDesignState, ids: string[]) => {
  const topLevelIds = selectTopLevelSelectedIds(state, ids);
  const groups = selectGroupsByIds(state, topLevelIds);
  if (groups.length === 0) return topLevelIds;

  const groupIds = new Set(groups.map(({ id }) => id));
  return [...topLevelIds.filter((id) => !groupIds.has(id)), ...groups.flatMap(getGroupItemIds)];
};

/*
 * Per-type selectors for the property panel. Each returns only the selected items of one type,
 * so with useShallow a panel keeps the same array (and skips re-rendering) while another type
 * is edited.
 */
const isSelected =
  (ids: ReadonlySet<string>) =>
  ({ id }: { id: string }): boolean =>
    ids.has(id);

export const selectSelectedHighwayAreas = (state: FloorplanDesignState, ids: ReadonlySet<string>) =>
  state.design.areas.highwayAreas.filter(isSelected(ids));

export const selectSelectedRackingAreas = (state: FloorplanDesignState, ids: ReadonlySet<string>) =>
  state.design.areas.rackingAreas.filter(isSelected(ids));

export const selectSelectedSingularStationAreas = (
  state: FloorplanDesignState,
  ids: ReadonlySet<string>,
) => state.design.areas.singularStationAreas.filter(isSelected(ids));

export const selectSelectedStationaryAreas = (
  state: FloorplanDesignState,
  ids: ReadonlySet<string>,
) => state.design.areas.stationaryAreas.filter(isSelected(ids));

export const selectSelectedManualAreas = (state: FloorplanDesignState, ids: ReadonlySet<string>) =>
  state.design.areas.manualAreas.filter(isSelected(ids));

export const selectSelectedWalls = (state: FloorplanDesignState, ids: ReadonlySet<string>) =>
  state.design.obstacles.filter((obstacle) => ids.has(obstacle.id) && isWallObstacle(obstacle));

export const selectSelectedRegularObstacles = (
  state: FloorplanDesignState,
  ids: ReadonlySet<string>,
) =>
  state.design.obstacles.filter((obstacle) => ids.has(obstacle.id) && isRegularObstacle(obstacle));
